import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CFormSelect,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";

import { fetchExpenses } from "../api/expenses";
import { assignReportRowExpense, fetchReport } from "../api/reports";
import { t } from "../i18n";

export default function ReportPage() {
    const { id } = useParams();

    const [report, setReport] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingRowId, setSavingRowId] = useState(null);
    const [expenseFilter, setExpenseFilter] = useState("all");

    const visibleRows = report
        ? report.rows
            .map((row, index) => ({ row, rowNumber: index + 1 }))
            .filter(({ row }) => {
                if (expenseFilter === "all") {
                    return true;
                }

                if (expenseFilter === "unassigned") {
                    return !row.expenseId;
                }

                return String(row.expenseId) === expenseFilter;
            })
        : [];

    useEffect(() => {
        let isMounted = true;

        async function loadReport() {
            try {
                const [loadedReport, loadedExpenses] = await Promise.all([
                    fetchReport(id),
                    fetchExpenses(),
                ]);

                if (isMounted) {
                    setReport(loadedReport);
                    setExpenses(loadedExpenses);
                    setError(null);
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [id]);

    async function handleExpenseChange(rowId, expenseId) {
        if (!expenseId) {
            return;
        }

        setError(null);
        setSavingRowId(rowId);

        try {
            const updatedReport = await assignReportRowExpense({
                reportId: report.id,
                rowId,
                expenseId,
            });

            setReport(updatedReport);
        } catch (assignError) {
            setError(assignError.message);
        } finally {
            setSavingRowId(null);
        }
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1>{report ? report.title : t("reports.report")}</h1>
                    <p className="text-body-secondary mb-0">
                        {report
                            ? t("reports.assignmentProgress", {
                                assigned: report.assignedRowCount,
                                total: report.rowCount,
                            })
                            : t("reports.loading")}
                    </p>
                </div>

                <CButton as={Link} color="secondary" variant="outline" to="/reports">
                    {t("reports.back")}
                </CButton>
            </div>

            {error && (
                <CAlert color="danger">
                    {error}
                </CAlert>
            )}

            {loading && (
                <div className="d-flex align-items-center gap-2 text-body-secondary">
                    <CSpinner size="sm" />
                    {t("reports.loading")}
                </div>
            )}

            {!loading && report && (
                <CCard>
                    <CCardHeader>
                        <strong>
                            {report.processed ? t("reports.processed") : t("reports.assignExpenses")}
                        </strong>
                    </CCardHeader>

                    <CCardBody>
                        {expenses.length === 0 && (
                            <CAlert color="warning">
                                {t("reports.noExpenses")}
                            </CAlert>
                        )}

                        <div className="mb-3" style={{ maxWidth: "320px" }}>
                            <label className="form-label" htmlFor="expense-filter">
                                {t("reports.filterByExpense")}
                            </label>
                            <CFormSelect
                                id="expense-filter"
                                value={expenseFilter}
                                onChange={(event) => setExpenseFilter(event.target.value)}
                            >
                                <option value="all">{t("reports.allExpenses")}</option>
                                <option value="unassigned">{t("reports.unassignedExpense")}</option>
                                {expenses.map((expense) => (
                                    <option key={expense.id} value={expense.id}>
                                        {expense.title}
                                    </option>
                                ))}
                            </CFormSelect>
                        </div>

                        <CTable responsive hover className="mb-0 align-middle">
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell scope="col">{t("reports.row")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.amount")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.currency")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.details")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.beneficiary")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.expense")}</CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>

                            <CTableBody>
                                {visibleRows.map(({ row, rowNumber }) => (
                                    <CTableRow
                                        color={row.expenseId ? undefined : "warning"}
                                        key={row.id}
                                    >
                                        <CTableDataCell>{rowNumber}</CTableDataCell>
                                        <CTableDataCell>{row.amount}</CTableDataCell>
                                        <CTableDataCell>{row.currency}</CTableDataCell>
                                        <CTableDataCell className="text-break">{row.details}</CTableDataCell>
                                        <CTableDataCell className="text-break">{row.beneficiary}</CTableDataCell>
                                        <CTableDataCell style={{ minWidth: "240px" }}>
                                            <CFormSelect
                                                aria-label={t("reports.expense")}
                                                color={row.expenseId ? undefined : "warning"}
                                                disabled={expenses.length === 0 || savingRowId === row.id}
                                                value={row.expenseId || ""}
                                                onChange={(event) =>
                                                    handleExpenseChange(row.id, event.target.value)
                                                }
                                            >
                                                <option value="">{t("reports.chooseExpense")}</option>
                                                {expenses.map((expense) => (
                                                    <option key={expense.id} value={expense.id}>
                                                        {expense.title}
                                                    </option>
                                                ))}
                                            </CFormSelect>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                                {visibleRows.length === 0 && (
                                    <CTableRow>
                                        <CTableDataCell className="text-center text-body-secondary" colSpan={6}>
                                            {t("reports.noRowsForExpense")}
                                        </CTableDataCell>
                                    </CTableRow>
                                )}
                            </CTableBody>
                        </CTable>
                    </CCardBody>
                </CCard>
            )}
        </>
    );
}
